import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-places-autocomplete',
  templateUrl: './places-autocomplete.component.html',
  styleUrls: ['./places-autocomplete.component.scss']
})
export class PlacesAutocompleteComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  @Input() placeholder = 'Buscar dirección...';
  @Input() set initialValue(value: string | null | undefined) {
    const normalizedValue = (value ?? '').trim();
    this._initialValue = normalizedValue;
    this.searchControl.setValue(normalizedValue, { emitEvent: false });
    if (!normalizedValue) {
      this.resetPredictions();
    }
  }

  get initialValue(): string {
    return this._initialValue;
  }

  @Input() countryCode = '';
  @Input() isRequired = false;
  @Input() isTouched: boolean | undefined = false;
  @Input() hasError = false;
  @Input() errorMessage = 'Este campo es obligatorio';
  @Input() noAbsolutePosition: boolean = false;

  @Output() placeSelected = new EventEmitter<string>();
  @Output() placeDetails = new EventEmitter<google.maps.places.PlaceResult | null>();
  @Output() inputBlur = new EventEmitter<void>();

  searchControl = new FormControl<string>('', { nonNullable: true });
  predictions: google.maps.places.AutocompletePrediction[] = [];
  showPredictions = false;
  isLoading = false;
  activePredictionIndex = -1;

  private placesService: google.maps.places.AutocompleteService | null = null;
  private placesDetailsService: google.maps.places.PlacesService | null = null;
  private readonly minimumSearchLength = 2;
  private readonly searchDebounceMs = 180;
  private requestSequence = 0;
  private _initialValue = '';
  private destroy$ = new Subject<void>();

  constructor(
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.initializeGooglePlacesServices();
    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countryCode'] && this.searchControl.value.trim().length >= this.minimumSearchLength) {
      this.getPredictions(this.searchControl.value.trim());
    }
  }

  onInputKeydown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    if (!this.showPredictions || this.predictions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActivePrediction(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActivePrediction(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const predictionToSelect = this.getPredictionToSelect();
      if (predictionToSelect) {
        this.selectPrediction(predictionToSelect);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.resetPredictions();
    }
  }

  onPredictionMouseEnter(index: number): void {
    this.activePredictionIndex = index;
  }

  getPredictionId(index: number): string {
    return `prediction-${index}`;
  }

  trackByPrediction(_index: number, prediction: google.maps.places.AutocompletePrediction): string {
    return prediction.place_id;
  }

  onFocus(): void {
    const value = this.searchControl.value.trim();
    if (value.length >= this.minimumSearchLength) {
      this.getPredictions(value);
    }
  }

  onBlur(): void {
    setTimeout(() => {
      this.resetPredictions();
      this.inputBlur.emit();
    }, 180);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.placeSelected.emit('');
    this.placeDetails.emit(null);
    this.resetPredictions();
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  selectPrediction(prediction: google.maps.places.AutocompletePrediction): void {
    this.searchControl.setValue(prediction.description, { emitEvent: false });
    this.placeSelected.emit(prediction.description);
    this.resetPredictions();

    if (prediction.place_id && this.placesDetailsService) {
      this.isLoading = true;
      this.placesDetailsService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name']
        },
        (placeResult, status) => {
          this.ngZone.run(() => {
            this.isLoading = false;
            if (status === google.maps.places.PlacesServiceStatus.OK && placeResult) {
              this.placeDetails.emit(placeResult);
            } else {
              this.placeDetails.emit(null);
            }
          });
        }
      );
    } else {
      this.placeDetails.emit(null);
    }
  }

  private initializeGooglePlacesServices(): void {
    if (typeof google === 'undefined' || !google.maps?.places) {
      this.placesService = null;
      this.placesDetailsService = null;
      return;
    }

    this.placesService = new google.maps.places.AutocompleteService();
    this.placesDetailsService = new google.maps.places.PlacesService(document.createElement('div'));
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((rawValue: string) => {
        const value = rawValue.trim();

        if (value.length >= this.minimumSearchLength) {
          this.getPredictions(value);
        } else {
          this.resetPredictions();
        }
      });
  }

  private getPredictions(input: string): void {
    if (!this.placesService) return;

    const currentRequestId = ++this.requestSequence;
    this.isLoading = true;

    const request: google.maps.places.AutocompletionRequest = {
      input,
      types: ['address']
    };

    if (this.countryCode) {
      request.componentRestrictions = { country: this.countryCode.toLowerCase() };
    }

    this.placesService.getPlacePredictions(request, (predictions, status) => {
      this.ngZone.run(() => {
        if (currentRequestId !== this.requestSequence) {
          return;
        }

        this.isLoading = false;

        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          this.predictions = predictions;
          this.showPredictions = true;
          this.activePredictionIndex = -1;
        } else {
          this.resetPredictions();
        }
      });
    });
  }

  private moveActivePrediction(step: 1 | -1): void {
    if (this.predictions.length === 0) {
      this.activePredictionIndex = -1;
      return;
    }

    if (this.activePredictionIndex === -1) {
      this.activePredictionIndex = step === 1 ? 0 : this.predictions.length - 1;
    } else {
      this.activePredictionIndex =
        (this.activePredictionIndex + step + this.predictions.length) % this.predictions.length;
    }

    this.scrollActivePredictionIntoView();
  }

  private getPredictionToSelect(): google.maps.places.AutocompletePrediction | null {
    if (this.predictions.length === 0) {
      return null;
    }

    if (this.activePredictionIndex >= 0 && this.activePredictionIndex < this.predictions.length) {
      return this.predictions[this.activePredictionIndex];
    }

    return this.predictions[0];
  }

  private getPredictionScore(
    prediction: google.maps.places.AutocompletePrediction,
    userInput: string
  ): number {
    const normalize = (text: string): string =>
      text.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

    const normalizedInput = normalize(userInput);
    const normalizedDescription = normalize(prediction.description);
    const normalizedMainText = normalize(prediction.structured_formatting.main_text || '');

    let score = 0;

    if (normalizedDescription === normalizedInput) score += 1000;
    if (normalizedMainText === normalizedInput) score += 1100;
    if (normalizedMainText.startsWith(normalizedInput)) score += 800;
    if (normalizedDescription.startsWith(normalizedInput)) score += 600;
    if (normalizedMainText.includes(normalizedInput)) score += 400;
    if (normalizedDescription.includes(normalizedInput)) score += 200;

    const matchedLength = prediction.matched_substrings
      ?.reduce((sum, item) => sum + item.length, 0) ?? 0;
    score += matchedLength * 10;

    score -= Math.abs(normalizedDescription.length - normalizedInput.length) * 0.5;

    return score;
  }

  private scrollActivePredictionIntoView(): void {
    if (this.activePredictionIndex < 0) {
      return;
    }

    const elementId = this.getPredictionId(this.activePredictionIndex);
    const activeElement = document.getElementById(elementId);
    activeElement?.scrollIntoView({ block: 'nearest' });
  }

  private resetPredictions(): void {
    this.predictions = [];
    this.showPredictions = false;
    this.activePredictionIndex = -1;
    this.isLoading = false;
    this.requestSequence += 1;
  }

  getFormattedMainText(prediction: google.maps.places.AutocompletePrediction): string {
    return prediction.structured_formatting.main_text || prediction.description;
  }

  getFormattedSecondaryText(prediction: google.maps.places.AutocompletePrediction): string {
    return prediction.structured_formatting.secondary_text || '';
  }

  isPredictionActive(index: number): boolean {
    return this.activePredictionIndex === index;
  }

  onEnterWithoutPredictions(event: Event): void {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    if (this.showPredictions && this.predictions.length > 0) {
      return;
    }

    const typedValue = this.searchControl.value.trim();
    if (!typedValue) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.placeSelected.emit(typedValue);
    this.placeDetails.emit(null);
  }
}